<script setup lang="ts">
import DiningIcon from '@/assets/icons/Menus/DiningIcon.vue';
import ScanIcon from '@/assets/icons/Profile/ScanIcon.vue';
import FileSource from '@/components/BaseComponents/FileSource.vue';
import { useFileUpload, type UploadOptions } from '@/composables/useFileUpload';
import { useOnboarding } from '@/composables/useOnboarding';
import type { MessageSchema } from '@/i18n/index.js';
import { useDishesStore, useRecipeStore } from '@/stores/dishesStore.js';
import { useUserStore } from '@/stores/useUserStore';
import type { CameraUploadPayload } from '@/types/camera';
import type { DishImportAnalyzeResponse } from '@/types/dish';
import SpeedDial from 'primevue/speeddial';
import { markRaw, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const emit = defineEmits<{
  (e: 'updateAnalyzeStatus', boolean): boolean;
}>();

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const router = useRouter();
const { uploadFile } = useFileUpload();
const onboarding = useOnboarding();

// Dialog visibility
const showDishDialog = ref(false);
const showRecipeDialog = ref(false);

const scanMenuItems = ref([
  {
    label: t('scans.scanDish'),
    icon: 'pi pi-image',
    customIcon: markRaw(DiningIcon),
    command: () => {
      showDishDialog.value = true;
    },
  },
  {
    label: t('scans.scanRecipe'),
    icon: 'pi pi-receipt',
    customIcon: markRaw(ScanIcon),
    command: () => {
      showRecipeDialog.value = true;
    },
  },
]);

function analyzeImage(payload: CameraUploadPayload, type: 'dish' | 'recipe') {
  const userStore = useUserStore();
  if (!userStore.validateUserCanGenerate()) return;

  emit('updateAnalyzeStatus', true);

  const uploadOptions: UploadOptions<DishImportAnalyzeResponse> = {
    endpoint: '/dishes/import/analyze',
    additionalData: { type },
    fieldName: 'file',
    transport: 'json-base64', // Force base64 for consistent processing
    onSuccess: (data: DishImportAnalyzeResponse) => {
      useRecipeStore().recipe = data.recipe;
      useDishesStore().selectedDish = data.dish;
      useDishesStore().shouldRefetch = true;

      const dishStore = useDishesStore();
      dishStore.selectedDish!.ingredients = dishStore.selectedDish!.ingredients.map((ing) => {
        if (ing.quantity === 0) ing.quantity = undefined;
        if (ing.quantity === undefined && ing.unit) ing.unit = '';
        return ing;
      });

      router.push({ path: `/menu/dishes/${dishStore.selectedDish!.id}/recipe-steps` });
    },
    onError: () => {
      emit('updateAnalyzeStatus', false);
    },
  };

  uploadFile<DishImportAnalyzeResponse>(payload, uploadOptions).finally(() => {
    emit('updateAnalyzeStatus', false);
  });
}

function handleDishFileUploaded(payload: CameraUploadPayload) {
  analyzeImage(payload, 'dish');
}

function handleRecipeFileUploaded(payload: CameraUploadPayload) {
  analyzeImage(payload, 'recipe');
}
</script>

<template>
  <div>
    <SpeedDial
      :model="scanMenuItems"
      :radius="120"
      type="linear"
      direction="up"
      :hide-on-click-outside="!onboarding.isOnboardingActive"
      class="onboarding-scan-details !fixed right-8"
      :style="{ bottom: 'calc(6rem + var(--safe-area-inset-bottom, 0px))' }"
      :pt="{
        root: { style: { 'align-items': 'flex-end' } },
        menu: { style: { 'align-items': 'flex-end' } },
      }"
    >
      <template #item="{ item, onClick }">
        <button
          class="my-1 flex items-center justify-end gap-3 rounded-full bg-surface-600 px-4 py-3 text-white hover:bg-surface-700"
          @click="onClick"
        >
          <p class="font-content text-sm text-white">{{ item.label }}</p>
          <component :is="item.customIcon" class="h-4 w-4 text-white" />
        </button>
      </template>
    </SpeedDial>

    <FileSource
      v-model:visible="showDishDialog"
      :header="t('scans.chooseSource')"
      :camera-accept="'image/*'"
      :gallery-accept="'image/*'"
      @file-uploaded="handleDishFileUploaded"
    />

    <FileSource
      v-model:visible="showRecipeDialog"
      :header="t('scans.chooseSource')"
      :camera-accept="'image/*'"
      :gallery-accept="'image/*'"
      @file-uploaded="handleRecipeFileUploaded"
    />
  </div>
</template>
